import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Archive,
  Edit3,
  FileText,
  Folder,
  FolderInput,
  FolderMinus,
  FolderOpen,
  Mic2,
  PlayCircle,
  RefreshCcw,
  Share2,
  Video,
} from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { runRef } from "@/lib/runNumber";

const ACTION_GLYPHS = {
  run_started: PlayCircle,
  narration_generated: FileText,
  narration_edited: Edit3,
  voiceover_generated: Mic2,
  stitch_video_generated: Video,
  share_created: Share2,
  share_revoked: Share2,
  share_accessed: Share2,
  asset_downloaded: FileText,
  workflow_created: Edit3,
  workflow_edited: Edit3,
  workflow_archived: Archive,
  workflow_unarchived: RefreshCcw,
  workflow_duplicated: Edit3,
  workflow_deleted: Archive,
  folder_created: Folder,
  folder_updated: Folder,
  folder_deleted: Folder,
  folder_item_added: FolderInput,
  folder_item_removed: FolderMinus,
  folder_workflow_item_added: FolderOpen,
  folder_workflow_item_removed: FolderMinus,
};

const ACTION_LABELS = {
  run_started: "ran the workflow",
  narration_generated: "generated narration",
  narration_edited: "edited narration",
  voiceover_generated: "generated voiceover",
  stitch_video_generated: "rendered final video",
  share_created: "created a share link",
  share_revoked: "revoked a share link",
  share_accessed: "share link opened",
  asset_downloaded: "downloaded an asset",
  workflow_created: "created the workflow",
  workflow_edited: "edited the workflow",
  workflow_archived: "archived the workflow",
  workflow_unarchived: "unarchived the workflow",
  workflow_duplicated: "duplicated the workflow",
  workflow_deleted: "deleted a workflow",
  folder_created: "created a folder",
  folder_updated: "updated a folder",
  folder_deleted: "deleted a folder",
  folder_item_added: "added a run to a folder",
  folder_item_removed: "removed a run from a folder",
  folder_workflow_item_added: "added a workflow to a folder",
  folder_workflow_item_removed: "removed a workflow from a folder",
};

import { timeAgo } from "@/lib/timeAgo";

function shortEmail(email) {
  if (!email) return "system";
  const [local] = email.split("@");
  return local || email;
}

function ActorAvatar({ email }) {
  const initials =
    (email || "?")
      .split("@")[0]
      .split(/[._\-+]/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  return (
    <span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-600">
      {initials}
    </span>
  );
}

/**
 * Activity feed widget — used both inline on the Workflow Detail page
 * (scoped to one workflow_id) and as a global tile at the top of /runs
 * (no `workflowId` prop → tenant-wide scope).
 *
 * Polls `/api/audit-events/recent` every 30s.  Backend scopes by RBAC:
 * viewer/presenter only see their own events; builder/admin see all.
 */
export default function RecentActivityWidget({
  workflowId,
  limit = 10,
  title = "Recent activity",
  emptyHint = "Nothing here yet.",
  compact = false,
}) {
  const params = new URLSearchParams();
  if (workflowId) params.set("workflow_id", workflowId);
  params.set("limit", String(limit));
  const qs = params.toString();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["recent-activity", workflowId || "_tenant_", limit],
    queryFn: async () =>
      (await api.get(`/audit-events/recent?${qs}`)).data,
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  const items = data?.items || [];
  const scopeLabel = data?.scope === "self" ? "your events" : "tenant-wide";

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
      data-testid={workflowId ? `workflow-activity-${workflowId}` : "global-activity"}
    >
      <header className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-slate-900">
          <Activity size={15} className="text-indigo-600" />
          {title}
        </h2>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">
          {scopeLabel}
        </span>
      </header>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full rounded-md" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-xs text-red-600">Could not load activity.</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid="activity-empty">
          {emptyHint}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="activity-list">
          {items.map((ev) => {
            const Glyph = ACTION_GLYPHS[ev.action] || Activity;
            const label = ACTION_LABELS[ev.action] || ev.action.replace(/_/g, " ");
            const actorName = shortEmail(ev.actor_email);
            return (
              <li
                key={ev.event_id}
                className="flex items-start gap-2 rounded-md border border-slate-100 px-2 py-1.5 text-[12px] leading-tight"
                data-testid={`activity-row-${ev.event_id}`}
              >
                <ActorAvatar email={ev.actor_email} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <Glyph size={11} className="flex-none text-slate-400" />
                    <span className="truncate text-slate-700">
                      <span className="font-medium text-slate-900">
                        {actorName}
                      </span>{" "}
                      {label}
                      {ev.metadata?.run_number != null && (
                        <>
                          {" for "}
                          <span
                            className="font-mono text-indigo-700"
                            data-testid={`activity-row-run-${ev.event_id}`}
                          >{runRef(ev.metadata.run_number)}</span>
                        </>
                      )}
                    </span>
                  </div>
                  {!compact && ev.metadata && (
                    <div className="truncate text-[10px] text-slate-400">
                      {metaSummary(ev)}
                    </div>
                  )}
                </div>
                <span className="flex-none text-[10px] text-slate-400">
                  {timeAgo(ev.timestamp)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function metaSummary(ev) {
  const m = ev.metadata || {};
  const bits = [];
  // R3-Phase-1 — run_number is rendered prominently in the row title
  // already; the sub-line only includes the workflow name and other
  // event-specific bits so it never duplicates the leading #NNNN.
  if (m.workflow_name) bits.push(m.workflow_name);
  if (m.persona) bits.push(`persona: ${m.persona}`);
  if (m.provider) bits.push(`provider: ${m.provider}`);
  if (m.voice_label) bits.push(`voice: ${m.voice_label}`);
  if (m.demo_pace) bits.push(`pace: ${m.demo_pace}`);
  if (m.token_prefix) bits.push(`token: ${m.token_prefix}…`);
  return bits.join(" · ");
}
